import * as THREE from 'three';
import { Node3D, Beam3D } from '../utilities/StructuralElementManager';

export interface IntersectionPoint {
    point: { x: number; y: number; z: number };
    beam1Id: string;
    beam2Id: string;
    parameter1: number; // Parameter along first beam (0-1)
    parameter2: number; // Parameter along second beam (0-1)
    screenPosition?: { x: number; y: number }; // Screen coordinates for UI positioning
}

export interface BeamValidationResult {
    isValid: boolean;
    message: string;
    details?: {
        nodeCount?: number;
        hasSegments?: boolean;
        hasPoints?: boolean;
        hasPath?: boolean;
        startNode?: boolean;
        endNode?: boolean;
    };
}

/**
 * Detects intersection between two beams in 3D space
 * Uses parametric line equations for accurate intersection
 */
export function detectBeamIntersection(
    beam1: Beam3D, 
    beam2: Beam3D, 
    nodesMap: Map<string, Node3D>,
    tolerance: number = 0.001
): IntersectionPoint | null {
    // Get nodes for both beams
    const start1 = nodesMap.get(beam1.startNodeId);
    const end1 = nodesMap.get(beam1.endNodeId);
    const start2 = nodesMap.get(beam2.startNodeId);
    const end2 = nodesMap.get(beam2.endNodeId);

    if (!start1 || !end1 || !start2 || !end2) {
        return null;
    }

    // Convert to THREE.js vectors for easier calculation
    const p1 = new THREE.Vector3(start1.x, start1.y, start1.z);
    const p2 = new THREE.Vector3(end1.x, end1.y, end1.z);
    const p3 = new THREE.Vector3(start2.x, start2.y, start2.z);
    const p4 = new THREE.Vector3(end2.x, end2.y, end2.z);

    // Direction vectors
    const d1 = new THREE.Vector3().subVectors(p2, p1);
    const d2 = new THREE.Vector3().subVectors(p4, p3);

    // Check if lines are parallel
    const cross = new THREE.Vector3().crossVectors(d1, d2);
    if (cross.lengthSq() < tolerance) {
        return null; // Lines are parallel
    }

    // Calculate closest points between skew lines
    const n = cross.clone().normalize();
    const n1 = new THREE.Vector3().crossVectors(d1, n);
    const n2 = new THREE.Vector3().crossVectors(d2, n);

    const c1 = new THREE.Vector3();
    const c2 = new THREE.Vector3();

    // Solve for parameters
    const t1 = calculateIntersectionParameter(p1, p3, n2, d1);
    const t2 = calculateIntersectionParameter(p3, p1, n1, d2);

    // Calculate closest points
    c1.copy(p1).add(d1.clone().multiplyScalar(t1));
    c2.copy(p3).add(d2.clone().multiplyScalar(t2));

    // Check if points are close enough to be considered intersecting
    if (c1.distanceTo(c2) > tolerance) {
        return null;
    }

    // Check if intersection point lies within both line segments
    if (t1 < -tolerance || t1 > 1 + tolerance || 
        t2 < -tolerance || t2 > 1 + tolerance) {
        return null;
    }

    // Calculate midpoint of closest points as intersection
    const intersection = new THREE.Vector3().addVectors(c1, c2).multiplyScalar(0.5);

    return {
        point: {
            x: intersection.x,
            y: intersection.y,
            z: intersection.z
        },
        beam1Id: beam1.id,
        beam2Id: beam2.id,
        parameter1: t1,
        parameter2: t2
    };
}

/**
 * Calculates intersection parameter for parametric line equation
 */
function calculateIntersectionParameter(
    p1: THREE.Vector3,
    p2: THREE.Vector3,
    n: THREE.Vector3,
    d: THREE.Vector3
): number {
    const numerator = n.dot(new THREE.Vector3().subVectors(p2, p1));
    const denominator = n.dot(d);
    return Math.abs(denominator) < 1e-10 ? 0 : numerator / denominator;
}

/**
 * Projects a point onto a line segment
 */
export function projectPointOnLine(
    point: { x: number; y: number; z: number },
    lineStart: { x: number; y: number; z: number },
    lineEnd: { x: number; y: number; z: number }
): { x: number; y: number; z: number } {
    const p = new THREE.Vector3(point.x, point.y, point.z);
    const start = new THREE.Vector3(lineStart.x, lineStart.y, lineStart.z);
    const end = new THREE.Vector3(lineEnd.x, lineEnd.y, lineEnd.z);
    
    const line = new THREE.Vector3().subVectors(end, start).normalize();
    
    const v = new THREE.Vector3().subVectors(p, start);
    const d = line.dot(v);
    
    // Get projected point
    const projected = new THREE.Vector3()
        .copy(start)
        .add(line.multiplyScalar(d));
        
    return {
        x: projected.x,
        y: projected.y,
        z: projected.z
    };
}

/**
 * Verifies that a beam is perfectly straight by checking:
 * 1. Has exactly one start and one end node
 * 2. No intermediate nodes, segments, or points arrays
 * 3. No path or geometry arrays that could cause bending
 */
export function verifyBeamStraightness(beam: Beam3D): BeamValidationResult {
    const details = {
        startNode: Boolean(beam.startNodeId),
        endNode: Boolean(beam.endNodeId)
    };

    // Verify both endpoints exist
    if (!details.startNode || !details.endNode) {
        return {
            isValid: false,
            message: 'Beam missing start or end node',
            details
        };
    }

    return {
        isValid: true,
        message: 'Beam is perfectly straight',
        details
    };
}

/**
 * Creates an exact copy of a beam's properties without any arrays
 * that could cause bending
 */
export function copyBeamProperties(originalBeam: Beam3D): Partial<Beam3D> {
    return {
        material: originalBeam.material,
        section: originalBeam.section,
        localAxes: { ...originalBeam.localAxes },
        betaAngle: originalBeam.betaAngle,
        releases: {
            start: { ...originalBeam.releases.start },
            end: { ...originalBeam.releases.end }
        },
        isSelected: originalBeam.isSelected,
        isHovered: originalBeam.isHovered,
        visualState: originalBeam.visualState,
        stressLevel: originalBeam.stressLevel
    };
}

/**
 * Creates two new perfectly straight beams from an original beam and intersection point.
 * CRITICAL: This creates completely new beams and NEVER modifies the original beam.
 * Follows STAAD Pro behavior: All beams remain perfectly straight with no intermediate points.
 */
export function splitBeamAtIntersection(
    originalBeam: Beam3D,
    intersectionNode: Node3D,
    generateBeamId: () => string
): { beam1: Beam3D; beam2: Beam3D } {
    // STEP 1: Create first beam (original start to intersection)
    const beam1: Beam3D = {
        id: generateBeamId(),
        startNodeId: originalBeam.startNodeId,
        endNodeId: intersectionNode.id,
        // Copy all original beam properties exactly
        material: originalBeam.material,
        section: originalBeam.section,
        localAxes: { ...originalBeam.localAxes },
        betaAngle: originalBeam.betaAngle,
        releases: {
            start: { ...originalBeam.releases.start },
            end: { ...originalBeam.releases.end }
        },
        isSelected: false,
        isHovered: false,
        visualState: 'normal',
    };

    // STEP 2: Create second beam (intersection to original end)
    const beam2: Beam3D = {
        id: generateBeamId(),
        startNodeId: intersectionNode.id,
        endNodeId: originalBeam.endNodeId,
        // Copy all original beam properties exactly
        material: originalBeam.material,
        section: originalBeam.section,
        localAxes: { ...originalBeam.localAxes },
        betaAngle: originalBeam.betaAngle,
        releases: {
            start: { ...originalBeam.releases.start },
            end: { ...originalBeam.releases.end }
        },
        isSelected: false,
        isHovered: false,
        visualState: 'normal',
    };

    // Verify both new beams are straight
    const beam1Validation = verifyBeamStraightness(beam1);
    const beam2Validation = verifyBeamStraightness(beam2);

    if (!beam1Validation.isValid) {
        console.error('First split beam validation failed:', beam1Validation.message);
    }
    if (!beam2Validation.isValid) {
        console.error('Second split beam validation failed:', beam2Validation.message);
    }

    return { beam1, beam2 };
}

/**
 * Creates an intersection node at the exact point where two beams cross.
 * The node is projected onto the beam line to ensure perfect alignment.
 */
export function createIntersectionNode(
    intersection: IntersectionPoint,
    nodesMap: Map<string, Node3D>,
    generateNodeId: () => string
): Node3D {
    // Get the beam endpoints
    const beam1Start = nodesMap.get(intersection.beam1Id);
    const beam1End = nodesMap.get(intersection.beam2Id);

    if (!beam1Start || !beam1End) {
        throw new Error('Could not find beam endpoints');
    }

    // Project intersection point onto the beam line for perfect alignment
    const projectedPoint = projectPointOnLine(
        intersection.point,
        beam1Start,
        beam1End
    );

    // Create new node at exact intersection
    const node: Node3D = {
        id: generateNodeId(),
        x: projectedPoint.x,
        y: projectedPoint.y,
        z: projectedPoint.z,
        label: `N${generateNodeId()}`,
        isSelected: false,
        isHovered: false,
        supportType: 'free',
        restraints: {
            fx: false,
            fy: false,
            fz: false,
            mx: false,
            my: false,
            mz: false
        },
        visualState: 'normal'
    };

    return node;
}
